import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { name: "", visibility: "Show", customer_group: "Wholesaler", countries: [], status: "Active" };

export default function ShippingRules() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [countriesInput, setCountriesInput] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get("/shipping-rules");
    setRows(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!form.name) return toast.error("Name required");
    const payload = {
      ...form,
      countries: countriesInput.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.post("/shipping-rules", payload);
      toast.success("Shipping rule created");
      setOpen(false);
      setForm(empty);
      setCountriesInput("");
      load();
    } catch { toast.error("Failed"); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this shipping rule?")) return;
    await api.delete(`/shipping-rules/${id}`);
    load();
  }

  async function toggle(row) {
    const status = row.status === "Active" ? "Inactive" : "Active";
    await api.patch(`/shipping-rules/${row.id}`, { status });
    load();
  }

  return (
    <div data-testid="shipping-rules-page">
      <PageHeader
        title="Shipping Rules"
        subtitle="Control shipping method visibility for wholesale customers"
        actions={
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="shipping-create-btn">
            <Plus size={14} /> Create shipping rule
          </button>
        }
      />

      <div className="b2b-card">
        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--brand-muted)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No shipping rules" subtitle="Add rules to show or hide shipping methods for wholesale customers." testId="shipping-empty" />
        ) : (
          <table className="b2b-table" data-testid="shipping-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Visibility</th>
                <th>Customer group</th>
                <th>Countries</th>
                <th>Status</th>
                <th>Created at</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`shipping-row-${r.id}`}>
                  <td className="font-medium">{r.name}</td>
                  <td>{r.visibility}</td>
                  <td>{r.customer_group || "—"}</td>
                  <td>{(r.countries || []).join(", ") || "All"}</td>
                  <td>
                    <label className="switch">
                      <input type="checkbox" checked={r.status === "Active"} onChange={() => toggle(r)} data-testid={`shipping-toggle-${r.id}`} />
                      <span className="slider" />
                    </label>
                    <span className={`ml-2 badge ${r.status === "Active" ? "badge-active" : "badge-inactive"}`}>{r.status}</span>
                  </td>
                  <td className="text-sm" style={{ color: "var(--brand-muted)" }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn-danger" onClick={() => remove(r.id)} data-testid={`shipping-delete-${r.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create shipping rule"
        testId="shipping-modal"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)} data-testid="shipping-cancel">Cancel</button>
            <button className="btn-primary" onClick={create} data-testid="shipping-save">Create rule</button>
          </>
        }
      >
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="shipping-name" />
          </div>
          <div>
            <label className="form-label">Visibility (Wholesale Shipping Visibility rule)</label>
            <select className="form-select" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="shipping-visibility">
              <option>Show</option>
              <option>Hide</option>
            </select>
          </div>
          <div>
            <label className="form-label">Customer group / tag</label>
            <input className="form-input" value={form.customer_group}
              onChange={(e) => setForm({ ...form, customer_group: e.target.value })} data-testid="shipping-group" />
          </div>
          <div>
            <label className="form-label">Countries (comma separated, blank = all)</label>
            <input className="form-input" placeholder="United States, Canada" value={countriesInput}
              onChange={(e) => setCountriesInput(e.target.value)} data-testid="shipping-countries" />
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="shipping-status">
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
